import { useMemo, useState } from 'react'
import { ChartLine, Table } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import DashboardTile from '../../../components/DashboardTile'
import WeekSwitcher from '../../../components/WeekSwitcher'
import SeasonSelect from '../../TeamDashboard/subpages/TeamLoot/components/SeasonSelect'
import { usePageInfo } from '../../../config/queries'
import AuditGraph from '../../TeamDashboard/subpages/TeamAudit/components/AuditGraph'
import WeeklyKeystones from './WeeklyKeystones'
import KeystoneTotals from './KeystoneTotals'
import KeystoneLevelFilter from './KeystoneLevelFilter'
import { useCharacterKeystoneGraph } from '../hooks/useCharacterKeystoneGraph'

interface Props {
  character?: wowaudit.Character
}

const KeystoneDungeons = ({ character }: Props) => {
  const {
    data: { currentSeason, currentExpansion },
  } = usePageInfo()

  const [view, setViewState] = useState<'list' | 'graph'>(
    () =>
      (localStorage.getItem('character-dashboard-keystone-view') as
        | 'list'
        | 'graph'
        | null) ?? 'list',
  )

  const setView = (value: 'list' | 'graph') => {
    setViewState(value)
    localStorage.setItem('character-dashboard-keystone-view', value)
  }
  const [weeksAgo, setWeeksAgo] = useState(0)
  const [selectedSeasonId, setSelectedSeasonId] = useState(
    currentSeason.id.toString(),
  )
  const [minKeyLevel, setMinKeyLevelState] = useState<number | undefined>(
    () => {
      const stored = localStorage.getItem('character-dashboard-min-key-level')
      return stored ? Number(stored) : undefined
    },
  )

  const setMinKeyLevel = (level?: number) => {
    setMinKeyLevelState(level)
    if (level === undefined) {
      localStorage.removeItem('character-dashboard-min-key-level')
    } else {
      localStorage.setItem('character-dashboard-min-key-level', String(level))
    }
  }

  const selectedSeason = useMemo(
    () =>
      currentExpansion.seasons.find(
        (season) => season.id.toString() === selectedSeasonId,
      ) ?? currentSeason,
    [currentExpansion.seasons, selectedSeasonId, currentSeason],
  )

  const graphData = useCharacterKeystoneGraph(
    character,
    selectedSeason,
    minKeyLevel,
  )

  return (
    <DashboardTile
      title={
        <span className='flex items-center gap-4'>
          Keystone dungeons
          <KeystoneLevelFilter
            minKeyLevel={minKeyLevel}
            setMinKeyLevel={setMinKeyLevel}
          />
          <Separator orientation='vertical' className='h-6' />
          <KeystoneTotals
            character={character}
            weeksAgo={weeksAgo}
            minKeyLevel={minKeyLevel}
          />
        </span>
      }
      size='medium'
      dataPresent={!!character}
      headerRight={
        <div className='flex items-center gap-2 h-8'>
          {view === 'list' ? (
            <WeekSwitcher
              weeksAgo={weeksAgo}
              setWeeksAgo={setWeeksAgo}
              size='xs'
            />
          ) : (
            <SeasonSelect
              className='w-50 h-6 font-normal text-sm'
              value={selectedSeasonId}
              setValue={(value) =>
                setSelectedSeasonId(value ?? currentSeason.id.toString())
              }
            />
          )}
          <Tabs
            value={view}
            onValueChange={(value) => setView(value as 'list' | 'graph')}
          >
            <TabsList className='h-6 min-h-0 p-0.5'>
              <TabsTrigger value='graph' className='h-5 px-1.5'>
                <ChartLine className='w-3.5 h-3.5' />
              </TabsTrigger>
              <TabsTrigger value='list' className='h-5 px-1.5'>
                <Table className='w-3.5 h-3.5' />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      }
    >
      {view === 'list' ? (
        <WeeklyKeystones
          character={character}
          weeksAgo={weeksAgo}
          minKeyLevel={minKeyLevel}
        />
      ) : (
        <div className='pt-1 pb-2'>
          <AuditGraph
            compact
            title={''}
            config={graphData.config}
            values={graphData.values}
          />
        </div>
      )}
    </DashboardTile>
  )
}

export default KeystoneDungeons
